// Locale + formatting preference keys — stored in the server-synced settings
// bag (useUserSettingsStore). All consumer components read via useLocale() and
// useCreditSymbol() hooks rather than touching these constants directly.

export const LOCALE_KEY = 'locale';
export const UNIT_SYSTEM_KEY = 'unit_system';
export const CREDIT_SYMBOL_KEY = 'credit_symbol';
export const MEASUREMENT_CONVERT_KEY = 'measurement_convert';
// UI language is deliberately independent of LOCALE_KEY: `locale` keeps
// driving number/date formatting + units (the region template), while
// `ui_language` picks the message catalog. 'auto' (the default) derives the
// catalog from the locale's language prefix — the pre-existing behaviour.
export const UI_LANGUAGE_KEY = 'ui_language';
// Report language steers GENERATED content (research runs, reports) — the
// backend reads it in userprefs.ResolveOutputLanguage between the per-run
// explicit override and the locale. Independent of `ui_language` (chrome) on
// purpose: a multilingual user can read a German UI and default to French
// reports. 'auto' (the default) keeps the pre-setting chain (locale →
// detection → en). Mirror of internal/userprefs/keys.go ReportLanguageKey.
export const REPORT_LANGUAGE_KEY = 'report_language';

export type UnitSystem = 'metric' | 'imperial';
export type CreditSymbol = '•' | 'cr' | '';
export type UiLanguage = 'auto' | 'en' | 'fr' | 'de' | 'es';
// Same value space as UiLanguage — both are "auto or a shipped catalog language".
export type ReportLanguage = UiLanguage;

export const DEFAULT_UI_LANGUAGE: UiLanguage = 'auto';
export const DEFAULT_REPORT_LANGUAGE: ReportLanguage = 'auto';
// Endonyms — each language named in itself, so the picker is readable
// whatever the current UI language is. Not catalog keys on purpose.
export const UI_LANGUAGE_OPTIONS: { value: Exclude<UiLanguage, 'auto'>; endonym: string }[] = [
  { value: 'en', endonym: 'English' },
  { value: 'fr', endonym: 'Français' },
  { value: 'de', endonym: 'Deutsch' },
  { value: 'es', endonym: 'Español' },
];

export const DEFAULT_CREDIT_SYMBOL: CreditSymbol = '•';
export const DEFAULT_UNIT_SYSTEM: UnitSystem = 'metric';

export interface RegionTemplate {
  /** ISO 3166-1 alpha-2 (lower case) — also the key for the country name. */
  id: string;
  locale: string;
  unitSystem: UnitSystem;
  creditSymbol: CreditSymbol;
}

export const REGION_TEMPLATES: RegionTemplate[] = [
  { id: 'de', locale: 'de-DE', unitSystem: 'metric', creditSymbol: '•' },
  { id: 'fr', locale: 'fr-FR', unitSystem: 'metric', creditSymbol: '•' },
  { id: 'gb', locale: 'en-GB', unitSystem: 'metric', creditSymbol: '•' },
  { id: 'us', locale: 'en-US', unitSystem: 'imperial', creditSymbol: '•' },
  { id: 'es', locale: 'es-ES', unitSystem: 'metric', creditSymbol: '•' },
  { id: 'it', locale: 'it-IT', unitSystem: 'metric', creditSymbol: '•' },
];

/**
 * The picker label for a region, in the viewer's UI language: the country
 * name from `Intl.DisplayNames`, a number sample formatted by the region's own
 * locale (the thing the choice actually changes), and the unit-system label the
 * caller has already translated. Built at render time rather than stored, so
 * no English string can leak into a German picker.
 */
export function regionTemplateLabel(
  region: RegionTemplate,
  displayLocale: string,
  unitLabel: string,
): string {
  let country = region.id.toUpperCase();
  try {
    country = new Intl.DisplayNames([displayLocale], { type: 'region' }).of(country) ?? country;
  } catch {
    // Intl.DisplayNames unavailable — the bare ISO code is still unambiguous.
  }
  const sample = new Intl.NumberFormat(region.locale, { minimumFractionDigits: 2 }).format(1234.56);
  return `${country} — ${sample} · ${unitLabel}`;
}

export function unitSystemForLocale(locale: string): UnitSystem {
  // The US is the only widely-imperial market. The UK is officially metric
  // (Celsius, kg) bar road miles — defaulting UK users to Fahrenheit/pounds is
  // more wrong than right, and they can still opt into imperial in settings.
  const l = locale.toLowerCase();
  return l.startsWith('en-us') ? 'imperial' : 'metric';
}

export function closestTemplate(locale: string): RegionTemplate {
  const l = locale.toLowerCase();
  // Tier 1: exact locale match always wins.
  const exact = REGION_TEMPLATES.find((t) => t.locale.toLowerCase() === l);
  if (exact) return exact;

  // Tier 2: language-prefix match — but never return a template whose unit
  // system contradicts unitSystemForLocale(locale). e.g. en-AU is metric, so it
  // must not fall onto the en-GB (imperial) template just because both are "en".
  const wantUnit = unitSystemForLocale(locale);
  const prefixMatch = REGION_TEMPLATES.find(
    (t) =>
      l.startsWith(t.locale.slice(0, 2).toLowerCase()) && t.unitSystem === wantUnit,
  );
  if (prefixMatch) return prefixMatch;

  // Tier 3: fall back to any template with the desired unit system, else the
  // metric default (de).
  return (
    REGION_TEMPLATES.find((t) => t.unitSystem === wantUnit) ??
    REGION_TEMPLATES.find((t) => t.id === 'de')!
  );
}
